import { useMemo, useState } from 'react';
import { Button, Checkbox, Select, Tooltip } from 'antd';
import SearchAutoComplete from '@copilot/common/components/search/autocomplete';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
	Account,
	AccountDashboardFilters,
	AccountStatusNames,
} from '@copilot/common/pages/accountDashboard/types';
import Title from '@copilot/common/typography/title';
import drawerManager from '../../utils/drawerManager/index';
import { IAccountManager } from '@copilot/data/graphql/_generated';
import styles from './accountDashboardHeader.module.less';
import { formatName } from '@copilot/common/utils';

export const SEARCH_INPUT_PLACEHOLDER = 'Search Account';
export const ACCOUNT_STATUS_SELECTOR_TOOLTIP =
	'Use this option to filter accounts based on their status.';
export const LINKEDIN_DISCONNECTED_CHECKBOX_TOOLTIP =
	'It will show only accounts with users that have their LinkedIn account disconnected.';

const ALL_FILTER = 'All';

export type AccountDashboardHeaderProps = Readonly<{
	/**
	 * The list of all account data
	 */
	accounts: Account[];
	/**
	 * user counts
	 */
	userCounts: {
		/**
		 * Number of active users
		 */
		activeUserCount: number;
		/**
		 * Total number of users
		 */
		totalUserCount: number;
	};
	/**
	 * Current applied filters
	 */
	filters: AccountDashboardFilters;
	/**
	 * True if account data is being fetched
	 */
	loading: boolean;
	/**
	 * Callback function to update filtering on account data
	 * @param filters
	 */
	updateFilterCallback: (filters: AccountDashboardFilters) => void;
	/**
	 * Called on creation of a new account
	 */
	onAccountCreated: (accountId: string) => unknown;
	/**
	 * List of account managers
	 */
	accountManagers?: Readonly<IAccountManager[]>;
}>;

type ExplainerProps = Readonly<{
	tooltipTitle: string;
}>;

function HelpInfo({ tooltipTitle }: ExplainerProps) {
	return (
		<Tooltip title={tooltipTitle}>
			<QuestionCircleOutlined className={styles.accountDashboardHeader__filter__help} />
		</Tooltip>
	);
}

export default function AccountDashboardHeader({
	accounts,
	accountManagers,
	updateFilterCallback,
	onAccountCreated,
	filters,
	loading,
	userCounts,
}: AccountDashboardHeaderProps) {
	const { totalUserCount, activeUserCount } = userCounts;
	const [searchKey, setSearchKey] = useState<string>('');
	const searchOptions = useMemo<{ key: string; value: string; label: string }[]>(
		() => accounts.map((acc) => ({ key: acc.id, label: acc.name, value: acc.name })),
		[accounts]
	);
	return (
		<header className={styles.accountDashboardHeader}>
			<div className={styles.accountDashboardHeader__title}>
				<Title level={3}>Accounts</Title>
				{loading ? null : (
					<span className={styles.accountDashboardHeader__title__details}>
						{activeUserCount} Active Users / {totalUserCount} Users
					</span>
				)}
				<Button
					type="primary"
					onClick={() =>
						drawerManager.openAccountCreationDrawer({
							getContainer: false,
							accountManagers,
							onAccountCreated: onAccountCreated,
						})
					}
				>
					Add account
				</Button>
			</div>
			<div className={styles.accountDashboardHeader__filters}>
				<div
					className={`${styles.accountDashboardHeader__filter} 
					${styles.accountDashboardHeader__filterLg}`}
				>
					<SearchAutoComplete
						options={searchOptions}
						placeholder={SEARCH_INPUT_PLACEHOLDER}
						onSearch={(value: string) => {
							updateFilterCallback({
								...filters,
								search: value,
								searchUppercase: value.toUpperCase(),
							});
						}}
						onChange={setSearchKey}
						value={searchKey}
						isLoadingOptions={loading}
					/>
				</div>
				{accountManagers && (
					<div
						className={`${styles.accountDashboardHeader__filter} 
					${styles.accountDashboardHeader__filterMd}`}
					>
						<Select
							className={styles.accountDashboardHeader__select}
							placeholder={'Account Manager'}
							options={[
								{ label: ALL_FILTER, value: ALL_FILTER },
								...accountManagers.map((a) => ({
									label: formatName(a.firstName, a.lastName),
									value: a.id,
								})),
							]}
							value={filters.accountManagerId}
							onChange={(value: string) => {
								const updated =
									value === ALL_FILTER
										? { ...filters, accountManagerId: undefined }
										: { ...filters, accountManagerId: value };
								updateFilterCallback(updated);
							}}
						/>
					</div>
				)}
				<div
					className={`${styles.accountDashboardHeader__filter} 
					${styles.accountDashboardHeader__filterSm}`}
				>
					<Select
						className={styles.accountDashboardHeader__select}
						options={[
							{ value: ALL_FILTER, label: ALL_FILTER },
							{
								value: AccountStatusNames.Active,
								label: AccountStatusNames.Active,
							},
							{
								value: AccountStatusNames.Deactivated,
								label: AccountStatusNames.Deactivated,
							},
						]}
						value={filters.accountStatus ? filters.accountStatus : ALL_FILTER}
						onChange={(value: string) => {
							const updated =
								value === ALL_FILTER
									? { ...filters, accountStatus: undefined }
									: { ...filters, accountStatus: value };
							updateFilterCallback(updated);
						}}
					/>
					<HelpInfo tooltipTitle={ACCOUNT_STATUS_SELECTOR_TOOLTIP} />
				</div>
				<div className={styles.accountDashboardHeader__filter}>
					<Checkbox
						checked={filters.linkedInDisconnected}
						onChange={(e: CheckboxChangeEvent) => {
							updateFilterCallback({
								...filters,
								linkedInDisconnected: e.target.checked,
							});
						}}
					>
						LinkedIn Disconnected
					</Checkbox>
					<HelpInfo tooltipTitle={LINKEDIN_DISCONNECTED_CHECKBOX_TOOLTIP} />
				</div>
			</div>
		</header>
	);
}
